import { Component, ChangeDetectionStrategy, signal, computed, inject, OnInit, OnDestroy, ElementRef, viewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { collection, doc, onSnapshot, setDoc, deleteDoc, query, where, getDocs, Unsubscribe, serverTimestamp } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from './firebase';
import { AuthService } from './auth.service';
import { NotificationService } from './notification';
import { ChatService } from './services/chat.service';
import { Header } from './header';
import { AuthProfile } from './models/auth.model';

export interface ContactItem {
  id: string;
  uid: string;
  displayName: string;
  username?: string;
  phoneNumber?: string;
  photoURL?: string;
  email?: string;
  accountType?: string;
  category: 'Family' | 'Friends' | 'Organisation' | 'Me' | string;
  bio?: string;
  isOnline?: boolean;
  createdAt?: unknown;
}

@Component({
  selector: 'app-contacts',
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    Header
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div 
      class="min-h-screen bg-transparent text-zinc-900 dark:text-zinc-50 flex flex-col font-sans relative selection:bg-ios-blue/20 overflow-x-hidden"
      (touchstart)="onBodyTouchStart($event)"
      (touchend)="onBodyTouchEnd($event)"
    >

      <!-- Header -->
      <app-header
        title="Contacts"
        subtitle="@contacts"
        iconName="contacts"
        (back)="goBack()"
      >
        <div actions class="flex items-center gap-2">
          <button
            type="button"
            (click)="openSearchModal()"
            class="h-9 px-3.5 rounded-full bg-ios-blue text-white font-bold text-xs flex items-center gap-1.5 shadow-md shadow-ios-blue/20 hover:bg-ios-blue/90 active:scale-95 transition-all cursor-pointer"
            title="Search & Add Contact"
          >
            <mat-icon class="!text-[18px]">person_add</mat-icon>
            <span class="hidden sm:inline">Add Contact</span>
          </button>
        </div>
      </app-header>

      <!-- Subheader Category Chips Bar -->
      <div class="ios-sub-header-liquid">
        <div class="max-w-4xl mx-auto w-full px-2 sm:px-4">
          <div
            #chipScrollContainer
            class="chip-scroll-container !gap-2.5 !py-2.5 !px-3 sm:!px-4 no-scrollbar justify-start sm:justify-center w-full overflow-x-auto scroll-smooth overscroll-x-contain touch-pan-x"
            (touchstart)="onChipTouchStart($event)"
            (touchend)="onChipTouchEnd($event)"
            (mousedown)="startDrag($event)"
            (mouseleave)="stopDrag()"
            (mouseup)="stopDrag()"
            (mousemove)="onDrag($event)"
          >
            @for (cat of categories; track cat) {
              <button
                type="button"
                [id]="'cat-chip-' + cat"
                (click)="selectCategory(cat)"
                class="px-3.5 py-1.5 rounded-full transition-all duration-300 shrink-0 border cursor-pointer active:scale-95 flex items-center gap-1.5 shadow-xs"
                [class.bg-ios-blue]="selectedCategory() === cat"
                [class.text-white]="selectedCategory() === cat"
                [class.border-ios-blue]="selectedCategory() === cat"
                [class.shadow-md]="selectedCategory() === cat"
                [class.shadow-ios-blue/20]="selectedCategory() === cat"
                [class.bg-black/5]="selectedCategory() !== cat"
                [class.dark:bg-white/5]="selectedCategory() !== cat"
                [class.text-zinc-600]="selectedCategory() !== cat"
                [class.dark:text-zinc-400]="selectedCategory() !== cat"
                [class.border-transparent]="selectedCategory() !== cat"
                [class.hover:bg-black/10]="selectedCategory() !== cat"
                [class.dark:hover:bg-white/10]="selectedCategory() !== cat"
                [title]="cat"
                [aria-label]="cat"
              >
                <!-- Both Icon and Text Label for Filter Chips -->
                <mat-icon class="!text-[16px] leading-none shrink-0">{{ getCategoryIcon(cat) }}</mat-icon>
                <span class="text-xs font-semibold leading-none">{{ cat }}</span>
                <span
                  class="text-[10px] px-1.5 py-0.2 rounded-full font-black"
                  [class.bg-white/20]="selectedCategory() === cat"
                  [class.text-white]="selectedCategory() === cat"
                  [class.bg-black/10]="selectedCategory() !== cat"
                  [class.dark:bg-white/10]="selectedCategory() !== cat"
                >
                  {{ getCountForCategory(cat) }}
                </span>
              </button>
            }
          </div>
        </div>
      </div>

      <!-- Main Body: Compact Google Contacts List -->
      <main class="flex-1 max-w-4xl mx-auto w-full p-4 md:p-6 relative z-10 pt-[124px] md:pt-[136px]">
        
        <!-- Loading State -->
        @if (isLoading()) {
          <div class="py-20 flex flex-col items-center justify-center text-center">
            <div class="w-12 h-12 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin mb-4"></div>
            <p class="text-xs font-black uppercase tracking-widest text-zinc-400">Loading Contacts...</p>
          </div>
        } @else {
          <!-- Add Contact Button above the user's default contact list item -->
          <div class="mb-3.5">
            <button
              type="button"
              id="btn-add-contact-above-list"
              (click)="openSearchModal()"
              class="w-full p-3.5 rounded-2xl border border-black/5 dark:border-white/10 flex items-center justify-between shadow-xs hover:shadow-md transition-all group bg-white/70 dark:bg-zinc-900/70 hover:bg-white/95 dark:hover:bg-zinc-800/95 backdrop-blur-xl cursor-pointer active:scale-[0.99]"
            >
              <div class="flex items-center gap-3.5 min-w-0">
                <div class="w-11 h-11 rounded-full bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform shadow-xs">
                  <mat-icon class="!text-[22px]">person_add</mat-icon>
                </div>
                <div class="text-left min-w-0">
                  <h4 class="text-sm font-bold text-zinc-900 dark:text-white group-hover:text-ios-blue transition-colors leading-tight">
                    Add Contact
                  </h4>
                  <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                    Search users by 10-digit phone number to add to your contacts
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <span class="text-xs font-bold text-ios-blue hidden sm:inline">Add</span>
                <div class="w-8 h-8 rounded-full bg-ios-blue/10 text-ios-blue group-hover:bg-ios-blue group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                  <mat-icon class="!text-[18px]">add</mat-icon>
                </div>
              </div>
            </button>
          </div>

          <!-- Contact Items Stream -->
          @if (filteredContacts().length > 0) {
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3.5 animate-ios-fade">
              @for (contact of filteredContacts(); track contact.id) {
                <div class="glass-card !rounded-2xl border border-black/5 dark:border-white/10 flex flex-col shadow-xs hover:shadow-md transition-all group bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl relative overflow-hidden">
                  
                  <!-- Top Row: Contact Info + Pin Icon in Top-Right Corner (or Delete Icon) -->
                  <div class="p-3.5 flex items-center justify-between gap-3 relative">
                    
                    <!-- Contact Avatar & Details -->
                    <div class="flex items-center gap-3.5 min-w-0 flex-1 cursor-pointer" [routerLink]="['/profile', contact.uid]">
                      <div class="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-tr from-ios-blue/15 to-ios-purple/15 dark:from-ios-blue/25 dark:to-ios-purple/25 shrink-0 border border-white/20 dark:border-white/10 relative shadow-xs">
                        @if (contact.photoURL) {
                          <img [src]="contact.photoURL" [alt]="contact.displayName" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" referrerpolicy="no-referrer" />
                        } @else {
                          <div class="w-full h-full flex items-center justify-center text-ios-blue font-black text-base">
                            {{ (contact.displayName || 'U').charAt(0).toUpperCase() }}
                          </div>
                        }
                        @if (contact.isOnline) {
                          <div class="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-zinc-900 z-10"></div>
                        }
                      </div>

                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5 min-w-0">
                          <h4 class="text-sm font-bold text-zinc-900 dark:text-white truncate group-hover:text-ios-blue transition-colors leading-tight">
                            {{ contact.displayName }}
                          </h4>

                          <!-- Only the icon itself, removing the circular green background -->
                          @if (!isMe(contact)) {
                            <mat-icon 
                              class="!text-[15px] leading-none text-zinc-400 dark:text-zinc-500 shrink-0 select-none" 
                              [title]="contact.category"
                            >{{ getCategoryIcon(contact.category) }}</mat-icon>
                          }
                        </div>
                        <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5 font-medium">
                          {{ contact.username ? '@' + contact.username : (contact.email || '@user') }}
                        </p>
                      </div>
                    </div>

                    <!-- Top-Right Corner Action: Pin Icon for Own Card or Delete Icon for Contacts -->
                    <div class="flex items-center gap-1 shrink-0">
                      @if (isMe(contact)) {
                        <!-- Pin Icon in Top-Right Corner -->
                        <div 
                          class="w-8 h-8 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center shadow-xs border border-ios-blue/20"
                          title="Pinned to top (You)"
                        >
                          <mat-icon class="!text-[18px]">push_pin</mat-icon>
                        </div>
                      } @else {
                        <button
                          type="button"
                          (click)="requestDeleteContact(contact)"
                          class="w-8 h-8 rounded-full text-zinc-400 hover:text-ios-red hover:bg-ios-red/10 flex items-center justify-center transition-all cursor-pointer active:scale-90"
                          title="Delete Contact"
                        >
                          <mat-icon class="!text-[18px]">delete</mat-icon>
                        </button>
                      }
                    </div>

                  </div>

                  <!-- Bottom Layer Action Row: Simplified Single Line with Voice, Video, and Chat -->
                  <div class="px-3 py-2 bg-black/5 dark:bg-white/5 border-t border-black/5 dark:border-white/5 grid grid-cols-3 gap-1 text-center">
                    <!-- 1. Voice (Inactive - removed word 'call') -->
                    <button
                      type="button"
                      disabled
                      class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl text-zinc-400 dark:text-zinc-500 opacity-50 cursor-not-allowed text-[11px] font-medium"
                      title="Voice feature inactive"
                    >
                      <mat-icon class="!text-[16px]">call</mat-icon>
                      <span>Voice</span>
                    </button>

                    <!-- 2. Video (Inactive - removed word 'call') -->
                    <button
                      type="button"
                      disabled
                      class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl text-zinc-400 dark:text-zinc-500 opacity-50 cursor-not-allowed text-[11px] font-medium"
                      title="Video feature inactive"
                    >
                      <mat-icon class="!text-[16px]">videocam</mat-icon>
                      <span>Video</span>
                    </button>

                    <!-- 3. Chat (Active) -->
                    <button
                      type="button"
                      (click)="startChat(contact)"
                      [disabled]="openingChatUid() === contact.uid"
                      class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl bg-ios-blue/10 hover:bg-ios-blue text-ios-blue hover:text-white font-bold text-[11px] transition-all active:scale-95 cursor-pointer disabled:opacity-50 shadow-xs"
                      title="Open Chat"
                    >
                      @if (openingChatUid() === contact.uid) {
                        <div class="w-3.5 h-3.5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                      } @else {
                        <mat-icon class="!text-[16px]">chat</mat-icon>
                      }
                      <span>Chat</span>
                    </button>
                  </div>

                </div>
              }
            </div>
          } @else {
            <!-- Empty State -->
            <div class="py-16 px-4 text-center max-w-md mx-auto glass-card !rounded-[32px] border border-black/5 dark:border-white/10 my-8 shadow-sm bg-white/40 dark:bg-black/40 backdrop-blur-2xl">
              <div class="w-16 h-16 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center mx-auto mb-4">
                <mat-icon class="!text-[32px]">contacts</mat-icon>
              </div>
              <h3 class="text-lg font-bold text-zinc-900 dark:text-white mb-1">
                No Contacts Found
              </h3>
              <p class="text-xs text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
                Search users by 10-digit phone number to add them directly to your personal contacts directory.
              </p>
              <button
                type="button"
                (click)="openSearchModal()"
                class="px-5 py-2.5 rounded-full bg-ios-blue text-white font-bold text-xs shadow-lg shadow-ios-blue/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer"
              >
                <mat-icon class="!text-[18px]">person_add</mat-icon>
                <span>Find & Add Contact</span>
              </button>
            </div>
          }
        }

      </main>

      <!-- Delete Confirmation Modal -->
      @if (contactToDelete(); as target) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div 
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="contactToDelete.set(null)"
            (keydown.escape)="contactToDelete.set(null)"
            tabindex="-1"
            role="button"
            aria-label="Close modal backdrop"
          ></div>

          <div class="relative w-full max-w-sm glass-card !rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-white/10 z-10 flex flex-col items-center text-center bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl animate-ios-slide-up">
            <div class="w-12 h-12 rounded-full bg-ios-red/10 text-ios-red flex items-center justify-center mb-3">
              <mat-icon class="!text-[24px]">delete_forever</mat-icon>
            </div>

            <h3 class="text-lg font-bold text-zinc-900 dark:text-white mb-1">Delete Contact?</h3>
            <p class="text-xs text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
              Are you sure you want to delete <strong class="text-zinc-900 dark:text-white">{{ target.displayName }}</strong> from your contacts directory?
            </p>

            <div class="flex items-center gap-3 w-full">
              <button
                type="button"
                (click)="contactToDelete.set(null)"
                class="flex-1 py-2.5 rounded-2xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-zinc-700 dark:text-zinc-200 font-bold text-xs transition-all active:scale-95 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                (click)="confirmDeleteContact()"
                [disabled]="isDeletingContact()"
                class="flex-1 py-2.5 rounded-2xl bg-ios-red text-white font-bold text-xs shadow-md shadow-ios-red/20 hover:bg-ios-red/90 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                @if (isDeletingContact()) {
                  <div class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                } @else {
                  <mat-icon class="!text-[16px]">delete</mat-icon>
                  <span>Delete</span>
                }
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Search & Add Contact Bottom Sheet View -->
      @if (showSearchModal()) {
        <div class="fixed inset-0 z-[1000] flex items-end justify-center p-0 sm:p-4 animate-ios-fade">
          <div
            class="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-xl transition-opacity"
            (click)="closeSearchModal()"
            (keydown.escape)="closeSearchModal()"
            tabindex="-1"
            role="button"
            aria-label="Close bottom sheet backdrop"
          ></div>

          <div
            class="relative w-full max-w-xl glass-card !rounded-t-[36px] sm:!rounded-t-[40px] !rounded-b-none p-6 shadow-2xl border-t border-x border-white/30 dark:border-white/15 z-10 flex flex-col gap-4 max-h-[90vh] overflow-y-auto bg-white/85 dark:bg-zinc-900/90 backdrop-blur-3xl animate-ios-slide-up relative mb-0"
            (click)="$event.stopPropagation()"
          >
            <!-- Bottom Sheet Drag Handle Indicator -->
            <div class="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full mx-auto shrink-0 mb-1 opacity-80"></div>

            <div class="relative z-10 flex flex-col gap-5">

            <!-- Modal Header -->
            <div class="flex items-center justify-between border-b border-black/5 dark:border-white/10 pb-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                  <mat-icon>person_search</mat-icon>
                </div>
                <div>
                  <h3 class="text-base font-bold text-zinc-900 dark:text-white leading-tight">Find Account by Phone</h3>
                  <p class="text-[11px] text-zinc-500 dark:text-zinc-400">Enter a 10-digit phone number for auto-search</p>
                </div>
              </div>
              <button
                type="button"
                (click)="closeSearchModal()"
                class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 text-zinc-500 hover:text-zinc-900 dark:hover:text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <mat-icon class="!text-[18px]">close</mat-icon>
              </button>
            </div>

            <!-- Phone Search Bar -->
            <div class="space-y-2">
              <label class="text-[11px] font-black uppercase tracking-wider text-zinc-500 dark:text-zinc-400 block px-1">
                10-Digit Phone Number
              </label>
              <div class="flex items-center gap-2">
                <div class="flex-1 ios-input-container !rounded-2xl !h-12 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-3 flex items-center gap-2">
                  <mat-icon class="text-zinc-400 !text-[20px]">phone</mat-icon>
                  <input
                    type="tel"
                    [value]="searchPhone"
                    (input)="onPhoneInput($any($event.target).value)"
                    placeholder="e.g. 9876543210"
                    maxlength="14"
                    class="w-full bg-transparent border-none outline-none text-sm font-bold text-zinc-900 dark:text-white placeholder:text-zinc-400"
                  />
                  @if (searchPhone) {
                    <button
                      type="button"
                      (click)="searchPhone = ''; searchPerformed.set(false); matchedAccount.set(null)"
                      class="text-zinc-400 hover:text-zinc-600"
                    >
                      <mat-icon class="!text-[16px]">close</mat-icon>
                    </button>
                  }
                </div>

                @if (isSearching()) {
                  <div class="w-10 h-10 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                    <div class="w-5 h-5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                  </div>
                }
              </div>
            </div>

            <!-- Result Box -->
            @if (searchPerformed()) {
              <div class="mt-2 space-y-4 animate-ios-slide">
                @if (matchedAccount(); as matched) {
                  <div class="space-y-3 bg-black/5 dark:bg-white/5 p-4 rounded-3xl border border-black/5 dark:border-white/10">
                    <div class="flex items-center justify-between">
                      <span class="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <mat-icon class="!text-[14px]">check_circle</mat-icon> Account Match Found
                      </span>
                      <span class="text-[10px] font-bold text-zinc-500 uppercase">
                        {{ matched.accountType === 'organization' ? 'Organization' : 'Personal' }}
                      </span>
                    </div>

                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-full overflow-hidden bg-ios-blue/10 shrink-0">
                        @if (matched.photoURL) {
                          <img [src]="matched.photoURL" [alt]="matched.displayName" class="w-full h-full object-cover" />
                        } @else {
                          <div class="w-full h-full flex items-center justify-center text-ios-blue font-bold">
                            {{ (matched.displayName || 'U').charAt(0).toUpperCase() }}
                          </div>
                        }
                      </div>
                      <div class="min-w-0 flex-1">
                        <h4 class="text-sm font-bold text-zinc-900 dark:text-white truncate">{{ matched.displayName }}</h4>
                        <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate">{{ matched.username ? '@' + matched.username : matched.email }}</p>
                      </div>
                    </div>

                    <!-- Category Selector for adding -->
                    <div class="space-y-1.5 pt-2">
                      <label class="text-[10px] font-black uppercase tracking-wider text-zinc-500 dark:text-zinc-400 block">
                        Assign Contact Category
                      </label>
                      <div class="flex flex-wrap gap-2">
                        @if (matched.accountType === 'organization') {
                          <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-blue text-white border border-ios-blue">
                            Organisation
                          </span>
                        } @else {
                          @for (cat of ['Friends', 'Family']; track cat) {
                            <button
                              type="button"
                              (click)="searchCategory.set(cat)"
                              class="px-3 py-1 rounded-full text-xs font-bold border transition-all cursor-pointer"
                              [class.bg-ios-blue]="searchCategory() === cat"
                              [class.text-white]="searchCategory() === cat"
                              [class.border-ios-blue]="searchCategory() === cat"
                              [class.bg-black/5]="searchCategory() !== cat"
                              [class.dark:bg-white/5]="searchCategory() !== cat"
                              [class.text-zinc-600]="searchCategory() !== cat"
                              [class.dark:text-zinc-400]="searchCategory() !== cat"
                              [class.border-transparent]="searchCategory() !== cat"
                            >
                              {{ cat }}
                            </button>
                          }
                        }
                      </div>
                    </div>

                    <!-- Add Button -->
                    <div class="pt-2">
                      @if (isAlreadyContact(matched.uid)) {
                        <div class="w-full py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold text-center flex items-center justify-center gap-1.5">
                          <mat-icon class="!text-[18px]">verified</mat-icon>
                          <span>Already in your contacts list</span>
                        </div>
                      } @else {
                        <button
                          type="button"
                          (click)="addContact(matched)"
                          [disabled]="isAddingContact()"
                          class="w-full py-3.5 rounded-2xl bg-ios-blue text-white font-bold text-xs shadow-lg shadow-ios-blue/20 hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          @if (isAddingContact()) {
                            <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Saving Contact...</span>
                          } @else {
                            <mat-icon class="!text-[18px]">person_add</mat-icon>
                            <span>Add to Contacts ({{ searchCategory() }})</span>
                          }
                        </button>
                      }
                    </div>

                  </div>
                } @else {
                  <!-- No Match Found -->
                  <div class="p-6 text-center bg-ios-red/5 rounded-3xl border border-ios-red/10 text-ios-red space-y-2">
                    <mat-icon class="!text-[32px] opacity-80">no_accounts</mat-icon>
                    <h4 class="text-sm font-bold">No Account Found</h4>
                    <p class="text-xs opacity-80 leading-relaxed max-w-xs mx-auto">
                      No registered user or organization was found with phone number <strong>{{ searchPhone }}</strong>.
                    </p>
                  </div>
                }
              </div>
            }

          </div>
        </div>
      </div>
      }

    </div>
  `,
  styles: [`
    .animate-ios-slide-up {
      animation: ios-slide-up 0.5s cubic-bezier(0.32, 0.72, 0, 1) forwards;
    }
    @keyframes ios-slide-up {
      from { transform: translateY(100%); opacity: 0.5; }
      to { transform: translateY(0); opacity: 1; }
    }
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }
    .no-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `]
})
export class ContactsView implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private chatService = inject(ChatService);

  openingChatUid = signal<string | null>(null);

  chipScrollContainer = viewChild<ElementRef<HTMLDivElement>>('chipScrollContainer');

  readonly categories = ['All', 'Organisation', 'Family', 'Friends'];
  selectedCategory = signal<string>('All');
  
  contacts = signal<ContactItem[]>([]);
  isLoading = signal<boolean>(true);

  // Deletion Confirmation Modal State
  contactToDelete = signal<ContactItem | null>(null);
  isDeletingContact = signal<boolean>(false);

  // Search Modal State
  showSearchModal = signal<boolean>(false);
  searchPhone = '';
  isSearching = signal<boolean>(false);
  matchedAccount = signal<AuthProfile | null>(null);
  searchCategory = signal<string>('Friends');
  searchPerformed = signal<boolean>(false);
  isAddingContact = signal<boolean>(false);

  private unsubscribeContacts: Unsubscribe | null = null;

  // Touch & Drag state for subheader chips & body swipe
  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;
  private chipTouchStartX = 0;
  private bodyTouchStartX = 0;

  filteredContacts = computed(() => {
    const cat = this.selectedCategory();
    const allContacts = this.contacts();
    const currentUser = this.authService.userProfile();

    let list = [...allContacts];

    // Find or synthesize current user's profile
    let myContactIndex = list.findIndex(c => this.isMe(c));
    let myContact: ContactItem | null = null;

    if (myContactIndex >= 0) {
      myContact = list.splice(myContactIndex, 1)[0];
    } else if (currentUser) {
      myContact = {
        id: 'me-' + currentUser.uid,
        uid: currentUser.uid,
        displayName: (currentUser.displayName || 'You') + ' (You)',
        username: currentUser.username || '',
        phoneNumber: currentUser.phoneNumber || '',
        photoURL: currentUser.photoURL || '',
        email: currentUser.email || '',
        accountType: currentUser.accountType || 'person',
        category: 'Me',
        bio: currentUser.bio || 'Note to self',
        isOnline: true
      };
    }

    // Filter remaining contacts by category
    let filtered: ContactItem[] = [];
    if (cat === 'All') {
      filtered = list;
    } else {
      filtered = list.filter(c => !this.isMe(c) && c.category === cat);
    }

    // Always PIN user's own profile card to top by default on 'All'
    if (myContact && cat === 'All') {
      filtered.unshift(myContact);
    }

    return filtered;
  });

  getCountForCategory(cat: string): number {
    const currentUser = this.authService.userProfile();
    let list = [...this.contacts()];
    let myContactPresent = list.some(c => this.isMe(c));

    if (cat === 'All') {
      return list.length + (myContactPresent || !currentUser ? 0 : 1);
    }
    return list.filter(c => !this.isMe(c) && c.category === cat).length;
  }

  getCategoryIcon(cat: string): string {
    switch (cat) {
      case 'All': return 'apps';
      case 'Organisation': return 'business';
      case 'Family': return 'family_restroom';
      case 'Friends': return 'people';
      default: return 'folder';
    }
  }

  selectCategory(cat: string) {
    this.selectedCategory.set(cat);
    this.scrollToActiveChip(cat);
  }

  scrollToActiveChip(cat: string) {
    setTimeout(() => {
      const container = this.chipScrollContainer()?.nativeElement;
      if (cat === 'All' && container) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      const chipEl = document.getElementById('cat-chip-' + cat);
      if (chipEl && container) {
        const containerRect = container.getBoundingClientRect();
        const chipRect = chipEl.getBoundingClientRect();
        const targetScrollLeft = container.scrollLeft + (chipRect.left - containerRect.left) - (containerRect.width / 2) + (chipRect.width / 2);
        container.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' });
      }
    }, 50);
  }

  isAlreadyContact(uid: string): boolean {
    return this.contacts().some(c => c.uid === uid);
  }

  isMe(contact: ContactItem): boolean {
    const currentUser = this.authService.userProfile();
    if (!currentUser) return false;
    if (contact.uid && currentUser.uid && contact.uid === currentUser.uid) return true;
    if (contact.phoneNumber && currentUser.phoneNumber && this.cleanDigits(contact.phoneNumber) === this.cleanDigits(currentUser.phoneNumber)) return true;
    return false;
  }

  isMeMatch(account: AuthProfile): boolean {
    const currentUser = this.authService.userProfile();
    if (!currentUser) return false;
    if (account.uid && currentUser.uid && account.uid === currentUser.uid) return true;
    if (account.phoneNumber && currentUser.phoneNumber && this.cleanDigits(account.phoneNumber) === this.cleanDigits(currentUser.phoneNumber)) return true;
    return false;
  }

  ngOnInit() {
    this.loadContacts();
    this.scrollToActiveChip(this.selectedCategory());
  }

  ngOnDestroy() {
    if (this.unsubscribeContacts) {
      this.unsubscribeContacts();
    }
  }

  loadContacts() {
    const currentUser = this.authService.user();
    if (!currentUser?.uid) {
      this.isLoading.set(false);
      return;
    }

    const contactsPath = `accounts/${currentUser.uid}/contacts`;
    const contactsRef = collection(db, contactsPath);

    this.unsubscribeContacts = onSnapshot(contactsRef, (snapshot) => {
      const list: ContactItem[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          uid: data['uid'] || docSnap.id,
          displayName: data['displayName'] || 'User',
          username: data['username'] || '',
          phoneNumber: data['phoneNumber'] || '',
          photoURL: data['photoURL'] || '',
          email: data['email'] || '',
          accountType: data['accountType'] || 'user',
          category: data['category'] || 'Friends',
          bio: data['bio'] || '',
          isOnline: data['isOnline'] === true,
          createdAt: data['createdAt']
        };
      });

      // Auto-save logic: for new/existing accounts, ensure user's own profile/phone is saved in contacts & pinned
      const userProf = this.authService.userProfile();
      if (userProf && userProf.uid) {
        const hasSelfDoc = snapshot.docs.some(d => d.id === userProf.uid || d.data()['uid'] === userProf.uid);
        if (!hasSelfDoc) {
          const selfDocRef = doc(db, 'accounts', userProf.uid, 'contacts', userProf.uid);
          setDoc(selfDocRef, {
            uid: userProf.uid,
            displayName: (userProf.displayName || 'You') + ' (You)',
            username: userProf.username || '',
            phoneNumber: userProf.phoneNumber || '',
            photoURL: userProf.photoURL || '',
            email: userProf.email || '',
            accountType: userProf.accountType || 'person',
            category: 'Me',
            bio: userProf.bio || 'Note to self',
            isPinned: true,
            createdAt: serverTimestamp()
          }, { merge: true }).catch(err => console.warn('Auto-save self contact info:', err));
        }
      }

      this.contacts.set(list);
      this.isLoading.set(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, contactsPath);
      this.isLoading.set(false);
    });
  }

  openSearchModal() {
    this.searchPhone = '';
    this.matchedAccount.set(null);
    this.searchPerformed.set(false);
    this.showSearchModal.set(true);
  }

  closeSearchModal() {
    this.showSearchModal.set(false);
  }

  cleanDigits(phone: string): string {
    return phone ? phone.replace(/\D/g, '') : '';
  }

  formatPhoneNumber(phone?: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    return phone;
  }

  onPhoneInput(value: string) {
    this.searchPhone = value;
    const digits = this.cleanDigits(value);
    if (digits.length === 10) {
      this.onPhoneSearch(digits);
    } else if (digits.length < 10) {
      this.searchPerformed.set(false);
      this.matchedAccount.set(null);
    }
  }

  async onPhoneSearch(rawDigits: string) {
    if (!rawDigits || rawDigits.length !== 10) return;

    this.isSearching.set(true);
    this.searchPerformed.set(false);
    this.matchedAccount.set(null);

    try {
      const accountsRef = collection(db, 'accounts');
      
      let snap = await getDocs(query(accountsRef, where('phoneNumber', '==', rawDigits)));
      
      if (snap.empty) {
        const formatted = this.formatPhoneNumber(rawDigits);
        snap = await getDocs(query(accountsRef, where('phoneNumber', '==', formatted)));
      }

      if (snap.empty) {
        snap = await getDocs(query(accountsRef, where('phone', '==', rawDigits)));
      }

      if (!snap.empty) {
        const firstDoc = snap.docs[0];
        const data = firstDoc.data() as Record<string, any>;
        const isOrg = data['accountType'] === 'organization' || Boolean(data['isOrganization']);
        const accountType = (isOrg ? 'organization' : 'person') as 'person' | 'organization';

        const account = {
          ...data,
          uid: firstDoc.id,
          displayName: data['displayName'] || 'User',
          photoURL: data['photoURL'] || '',
          email: data['email'] || '',
          accountType: accountType
        } as AuthProfile;

        if (accountType === 'organization') {
          this.searchCategory.set('Organisation');
        } else {
          this.searchCategory.set('Friends');
        }

        this.matchedAccount.set(account);
      } else {
        this.matchedAccount.set(null);
      }
    } catch (error) {
      console.error('Error searching accounts by phone:', error);
      handleFirestoreError(error, OperationType.GET, 'accounts');
    } finally {
      this.isSearching.set(false);
      this.searchPerformed.set(true);
    }
  }

  async addContact(account: AuthProfile) {
    const currentUser = this.authService.user();
    if (!currentUser?.uid || !account.uid) {
      this.notificationService.error('Please sign in to manage contacts.');
      return;
    }

    this.isAddingContact.set(true);
    const targetPath = `accounts/${currentUser.uid}/contacts/${account.uid}`;

    try {
      const contactDocRef = doc(db, 'accounts', currentUser.uid, 'contacts', account.uid);
      await setDoc(contactDocRef, {
        uid: account.uid,
        displayName: account.displayName || 'User',
        username: account.username || '',
        phoneNumber: account.phoneNumber || this.searchPhone || '',
        photoURL: account.photoURL || '',
        email: account.email || '',
        accountType: account.accountType || 'person',
        category: this.searchCategory() || 'Friends',
        bio: account.bio || '',
        createdAt: serverTimestamp()
      });

      this.notificationService.success(`Added ${account.displayName} to ${this.searchCategory()} contacts!`);
      this.closeSearchModal();
    } catch (error) {
      console.error('Error adding contact:', error);
      handleFirestoreError(error, OperationType.WRITE, targetPath);
      this.notificationService.error('Failed to add contact.');
    } finally {
      this.isAddingContact.set(false);
    }
  }

  requestDeleteContact(contact: ContactItem) {
    this.contactToDelete.set(contact);
  }

  async confirmDeleteContact() {
    const target = this.contactToDelete();
    if (!target) return;

    const currentUser = this.authService.user();
    if (!currentUser?.uid) return;

    this.isDeletingContact.set(true);
    const path = `accounts/${currentUser.uid}/contacts/${target.id}`;

    try {
      if (!target.id.startsWith('me-')) {
        await deleteDoc(doc(db, 'accounts', currentUser.uid, 'contacts', target.id));
      }
      this.notificationService.success(`Removed ${target.displayName} from contacts.`);
    } catch (error) {
      console.error('Error removing contact:', error);
      handleFirestoreError(error, OperationType.DELETE, path);
      this.notificationService.error('Failed to remove contact.');
    } finally {
      this.isDeletingContact.set(false);
      this.contactToDelete.set(null);
    }
  }

  toAuthProfile(contact: ContactItem): AuthProfile {
    return {
      uid: contact.uid,
      displayName: contact.displayName,
      username: contact.username || '',
      email: contact.email || '',
      photoURL: contact.photoURL || '',
      phoneNumber: contact.phoneNumber || '',
      bio: contact.bio || '',
      isOnline: contact.isOnline || false,
      accountType: (contact.accountType === 'organization' ? 'organization' : 'person') as 'person' | 'organization'
    } as AuthProfile;
  }

  async startChat(contact: ContactItem) {
    if (!contact?.uid) return;
    this.openingChatUid.set(contact.uid);
    try {
      const chatId = await this.chatService.getOrCreateChat(
        contact.uid,
        contact.displayName || 'User',
        contact.photoURL || ''
      );
      this.router.navigate(['/chat', chatId], {
        queryParams: {
          uid: contact.uid,
          name: contact.displayName || 'User',
          photo: contact.photoURL || ''
        }
      });
    } catch (err) {
      console.error('Error starting chat with contact:', err);
      this.notificationService.error('Failed to open conversation.');
    } finally {
      this.openingChatUid.set(null);
    }
  }

  private location = inject(Location);

  goBack() {
    const hasPreviousPage =
      typeof window !== 'undefined' &&
      window.history.length > 1 &&
      (window.history.state?.navigationId === undefined || window.history.state.navigationId > 1);
    if (hasPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(['/search']);
    }
  }

  // --- Touch & Drag Swipe Navigation Helpers ---
  onBodyTouchStart(e: TouchEvent) {
    this.bodyTouchStartX = e.touches[0].clientX;
  }

  onBodyTouchEnd(e: TouchEvent) {
    const deltaX = e.changedTouches[0].clientX - this.bodyTouchStartX;
    if (Math.abs(deltaX) > 60) {
      const idx = this.categories.indexOf(this.selectedCategory());
      if (deltaX < 0 && idx < this.categories.length - 1) {
        this.selectCategory(this.categories[idx + 1]);
      } else if (deltaX > 0 && idx > 0) {
        this.selectCategory(this.categories[idx - 1]);
      }
    }
  }

  onChipTouchStart(e: TouchEvent) {
    this.chipTouchStartX = e.touches[0].clientX;
  }

  onChipTouchEnd(e: TouchEvent) {
    const deltaX = e.changedTouches[0].clientX - this.chipTouchStartX;
    if (Math.abs(deltaX) > 40) {
      const idx = this.categories.indexOf(this.selectedCategory());
      if (deltaX < 0 && idx < this.categories.length - 1) {
        this.selectCategory(this.categories[idx + 1]);
      } else if (deltaX > 0 && idx > 0) {
        this.selectCategory(this.categories[idx - 1]);
      }
    }
  }

  startDrag(e: MouseEvent) {
    const el = this.chipScrollContainer()?.nativeElement;
    if (!el) return;
    this.isDragging = true;
    this.startX = e.pageX - el.offsetLeft;
    this.scrollLeft = el.scrollLeft;
  }

  stopDrag() {
    this.isDragging = false;
  }

  onDrag(e: MouseEvent) {
    if (!this.isDragging) return;
    e.preventDefault();
    const el = this.chipScrollContainer()?.nativeElement;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    el.scrollLeft = this.scrollLeft - walk;
  }
}
